import * as React from 'react';
const { useState, useCallback, useMemo, useEffect } = React;
import * as Autosuggest from 'react-autosuggest';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import Popper from '@material-ui/core/Popper';
import InputAdornment from '@material-ui/core/InputAdornment';
import Grow from '@material-ui/core/Grow';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { highlight } from '../../utils';
import TextField, { TextFieldProps } from '../text-field';
import IconButton from '../icon-button';
import Icon from '../icon';
import Box from '../box';
import {
	Root,
	Suggestion,
} from './styled';


type AutosuggestPart = {
	text: string;
	highlight: boolean
};

export type AutocompleteProps<T> = {
	value: T;
	dataSource: Array<T>;
	getSourceText: (item: T) => string;
	renderItem?: (item: unknown, parts: Array<AutosuggestPart>) => React.ReactNode;
	fixPopoverHorizontalCoords?: boolean;
	filterFn?: (item: unknown, str: string) => boolean;
	maxRenderingLimit?: number;
	onChange: (e: React.SyntheticEvent<any>, item: T) => void;
} & Omit<TextFieldProps, 'value' | 'onChange'>

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      position: 'relative',
    },
    suggestionsContainerOpen: {
      position: 'absolute',
      zIndex: 1,
      marginTop: theme.spacing(1),
      left: 0,
      right: 0,
    },
    suggestion: {
      display: 'block',
    },
    suggestionsList: {
      margin: 0,
      padding: 0,
      listStyleType: 'none',
    },
    divider: {
      height: theme.spacing(2),
    },
  }),
);

type AutocompleteComponent<T = any> = React.SFC<AutocompleteProps<T>>

const Autocomplete: AutocompleteComponent = (props) => {
	const {
		value,
		label,
		dataSource,
		readonly,
		fullWidth,
		getSourceText,
		renderItem,
		fixPopoverHorizontalCoords,
		maxRenderingLimit,
		filterFn,
		onChange,
		...rest
	} = props;
	const scope = useMemo(() => ({ isMounted: false }), []);
	const classes = useStyles({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [textValue, setTextValue] = useState(value ? getSourceText(value): '');
	const [stateSuggestions, setSuggestions] = useState<Array<any>>(dataSource);
	const [isFocused, setIsFocused] = useState(false);

	useEffect(() => {
		scope.isMounted = true;
		return () => scope.isMounted = false;
	}, []);

	useEffect(() => {
		if (scope.isMounted) {
			setTextValue(value ? getSourceText(value) : '');
		}
	}, [value]);

	const filterSuggestions = (value: string, dataSource: Array<any> = []) => {
		const inputValue = value.trim().toLowerCase();
		const inputLength = inputValue.length;
		const list = inputLength === 0
			? [...dataSource]
			: dataSource.filter(sourceItem => {
				if (typeof filterFn === 'function' ? filterFn(sourceItem, inputValue) : getSourceText(sourceItem).toLowerCase().indexOf(inputValue.toLowerCase()) !== -1) {
					return true
				}

				return false;
			});
				
		return list;
	};
	
  const handleSuggestionsFetchRequested = ({ value }: any) => {
    setSuggestions(filterSuggestions(value, dataSource));
  };

  const handleSuggestionsClearRequested = () => {
		setSuggestions([]);
  };

	const handleChange = (e: React.ChangeEvent<{}>, { newValue }: Autosuggest.ChangeEvent) => {
		setTextValue(newValue);
	}
	
	const handleSuggestionSelected = (e: React.ChangeEvent<{}>, { suggestion }) => {
		onChange(e, suggestion);
	};

	const handleToggleOpen = useCallback(() => {
		setIsFocused(!isFocused);
		setTimeout(() => !isFocused && anchorEl.focus());
	}, [isFocused, anchorEl]);

	const handleClear = useCallback((e: React.SyntheticEvent) => onChange(e, null), []);

	const renderInputComponent = (inputProps: any) => {
		const { classes, inputRef = () => {}, ref, ...other } = inputProps;

		return (
			<TextField
				{...rest}
				readonly={readonly}
				fullWidth
				InputProps={{
					endAdornment: (
						<InputAdornment position='end' style={{position: 'absolute', right: 0}}>
							<Box display='inline-flex' alignItems='center'>
								<IconButton onClick={handleClear}>
									<Icon type='clear' width={14} height={14} />
								</IconButton>
								<Icon
									type='arrow-drop-down'
									color='muted'
									width={21}
									height={21}
									onClick={handleToggleOpen}
								/>
							</Box>
						</InputAdornment>
					),
					inputRef: node => {
						ref(node);
						inputRef(node);
					},
					classes: {
						input: classes.input,
					}
				}}
				{...other}
				onFocus={(e) => {
					other.onFocus(e);
					setTimeout(() => setIsFocused(true), 100);
				}}
				onBlur={(e) => {
					const needReset = !textValue || !value || getSourceText(value) !== textValue;

					if (needReset) {
						onChange(e, null); 
						setTextValue('');
					}

					other.onBlur(e);
					setTimeout(() => scope.isMounted && setIsFocused(false), 200);
				}}
			/>
		);
	};

	const renderSuggestion = (sourceItem, { query, isHighlighted }: Autosuggest.RenderSuggestionParams) => {
		const matches = highlight.match(getSourceText(sourceItem), query);
		const parts = highlight
			.parse(getSourceText(sourceItem), matches)
			.map(p => {
				p.text = p.text.replace(/\s/g, '\u00A0');
				return p;
			});

		return (
			<MenuItem
				selected={isHighlighted}
				component='div'
				style={{ minHeight: 24, whiteSpace: 'normal', lineHeight: 1.6 }}>
				{
					typeof renderItem === 'function'
					?	renderItem(sourceItem, parts)
					:	parts.map((part, idx) => (
						<Suggestion key={idx} highlight={part.highlight}>
							{part.text}
						</Suggestion>
					))
				}
			</MenuItem>
		);
	};

  const autosuggestProps = {
    suggestions: maxRenderingLimit > 0 ? stateSuggestions.slice(0, maxRenderingLimit) : stateSuggestions,
    onSuggestionsFetchRequested: handleSuggestionsFetchRequested,
		onSuggestionsClearRequested: handleSuggestionsClearRequested,
		onSuggestionSelected: handleSuggestionSelected,
    getSuggestionValue: getSourceText,
		renderSuggestion,
		renderInputComponent,
		shouldRenderSuggestions: () => true,
	};

  return (
    <Root fullWidth={fullWidth}>
      <Autosuggest
				{...autosuggestProps}
        inputProps={{
          classes,
          label,
          value: textValue,
          onChange: handleChange,
          inputRef: (node: HTMLInputElement | null) => {
            setAnchorEl(node);
          },
          InputLabelProps: {
            shrink: true,
          },
        }}
        theme={{
          suggestionsList: classes.suggestionsList,
          suggestion: classes.suggestion,
        }}
        renderSuggestionsContainer={options => {
					const isOpen = !readonly && Boolean(options.children);

					return (
						<Popper
							anchorEl={anchorEl}
							open={isOpen}
							transition
							style={{ zIndex: 1300, marginLeft: fixPopoverHorizontalCoords ? 14 : undefined }}>
							{
								({ TransitionProps, placement }) => (
									<Grow
										{...TransitionProps}
										style={{
											transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
										}}>
										<Paper
											square
											elevation={4}
											{...options.containerProps}
											style={{
												width: anchorEl ? anchorEl.clientWidth : undefined,
												maxHeight: 400,
												paddingTop: 8,
												paddingBottom: 8,
												overflow: 'auto',
											}}>
											{options.children}
										</Paper>
									</Grow>
								)
							}
						</Popper>
					)}
				}
      />
    </Root>
  );
}

Autocomplete.defaultProps = {
	getSourceText: item => item.name,
	margin: 'bottom-small',
}

export default Autocomplete;
